/**
 * OutcomeCaptureForm — writes a HistoricalBid for a tender's terminal state.
 *
 * Mirrors PRD §7.6 (AC-O-1..3):
 *   - outcome (won / lost / bid_submitted / l2_l3 / withdrawn)
 *   - our_rank + our_bid_value_inr
 *   - top_bidders as a structured array of {rank, name, value_inr, is_us}
 *
 * The backend auto-derives the legacy competitor_won + l1_value_inr denorm
 * from the rank-1 non-us entry; users don't have to fill them.
 *
 * Usage:
 *   <OutcomeCaptureForm rfpId={rfpId} clientName={clientName} bidName={...} />
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Trophy, ArrowUp, ArrowDown } from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const OUTCOMES = [
  { value: "won", label: "Won", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "lost", label: "Lost", color: "bg-rose-100 text-rose-700 border-rose-200" },
  { value: "bid_submitted", label: "Submitted (awaiting result)", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "l2_l3", label: "L2 / L3", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "withdrawn", label: "Withdrawn", color: "bg-slate-100 text-slate-600 border-slate-200" },
];

const emptyBidder = (rank) => ({
  rank,
  name: "",
  value_inr: "",
  is_us: false,
});

export default function OutcomeCaptureForm({
  rfpId,
  clientName = "",
  bidName = "",
  onSaved,
}) {
  const [form, setForm] = useState({
    bid_name: bidName || "",
    client_name: clientName || "",
    outcome: "won",
    our_rank: "L1",
    our_bid_value_inr: "",
  });
  const [bidders, setBidders] = useState([
    { ...emptyBidder(1), is_us: true },
    emptyBidder(2),
    emptyBidder(3),
  ]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const renumberAndSet = (arr) => {
    setBidders(arr.map((b, i) => ({ ...b, rank: i + 1 })));
  };

  const addBidder = () => {
    setBidders((curr) => [...curr, emptyBidder(curr.length + 1)]);
  };

  const removeBidder = (idx) => {
    const next = bidders.filter((_, i) => i !== idx);
    renumberAndSet(next);
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= bidders.length) return;
    const next = [...bidders];
    [next[idx], next[target]] = [next[target], next[idx]];
    renumberAndSet(next);
  };

  const update = (idx, patch) => {
    setBidders((curr) => curr.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  const submit = async () => {
    if (!form.bid_name.trim()) {
      toast.error("Bid / tender name is required");
      return;
    }
    if (!form.client_name.trim()) {
      toast.error("Client name is required");
      return;
    }

    // Clean & validate top_bidders.
    const cleaned = bidders
      .map((b, i) => ({
        rank: i + 1,
        name: (b.name || "").trim(),
        value_inr: b.value_inr === "" || b.value_inr === null ? null : Number(b.value_inr),
        is_us: !!b.is_us,
      }))
      .filter((b) => b.name);

    const usCount = cleaned.filter((b) => b.is_us).length;
    if (usCount === 0) {
      toast.error("Mark which bidder is AXISINDIA (us)");
      return;
    }
    if (usCount > 1) {
      toast.error("Only one bidder can be marked AXISINDIA");
      return;
    }

    setBusy(true);
    try {
      const payload = {
        bid_name: form.bid_name.trim(),
        client_name: form.client_name.trim(),
        outcome: form.outcome,
        our_rank: form.our_rank.trim() || null,
        our_bid_value_inr:
          form.our_bid_value_inr === "" ? null : Number(form.our_bid_value_inr),
        top_bidders: cleaned,
        rfp_ref: rfpId || null,
      };
      const row = await v2Api.historicalBids.create(payload);
      setCreated(row);
      toast.success(
        `Captured · ${row.outcome.toUpperCase()}${
          row.competitor_won ? ` · L1 = ${row.competitor_won}` : ""
        }`,
      );
      onSaved?.(row);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    const outcomeMeta = OUTCOMES.find((o) => o.value === created.outcome);
    return (
      <Card>
        <CardContent className="py-6 space-y-3">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-emerald-500" />
            <h2 className="text-lg font-semibold text-slate-800">Outcome captured</h2>
            <Badge className={`border ${outcomeMeta?.color || ""}`}>
              {outcomeMeta?.label || created.outcome}
            </Badge>
          </div>
          <p className="text-sm text-slate-600">
            Historical bid <code className="text-xs bg-slate-100 px-1 rounded">{created.id}</code>{" "}
            saved.{" "}
            {created.competitor_won && (
              <>
                Auto-derived <strong>L1</strong> = {created.competitor_won}
                {created.l1_value_inr != null &&
                  ` @ ₹${new Intl.NumberFormat("en-IN", {
                    maximumFractionDigits: 0,
                  }).format(created.l1_value_inr)}`}
                .
              </>
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreated(null)}>
              Capture another
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="py-5 space-y-4">
        <div className="flex items-start gap-2">
          <Trophy className="w-5 h-5 text-slate-400 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Capture outcome</h2>
            <p className="text-xs text-slate-500">
              Records won / lost + top-3 bidders. The server derives the legacy
              L1-competitor-and-price from your rank-1 non-AXISINDIA row, so leave
              those fields blank in the form.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-7">
            <label className="text-xs text-slate-500">Bid / tender name</label>
            <Input
              value={form.bid_name}
              onChange={(e) => setForm({ ...form, bid_name: e.target.value })}
              placeholder="e.g. JJM 2026 — Maharashtra CEMS supply"
            />
          </div>
          <div className="col-span-5">
            <label className="text-xs text-slate-500">Client</label>
            <Input
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="e.g. Indian Oil Corporation Ltd"
            />
          </div>
          <div className="col-span-5">
            <label className="text-xs text-slate-500">Outcome</label>
            <select
              value={form.outcome}
              onChange={(e) => setForm({ ...form, outcome: e.target.value })}
              className="w-full px-2 py-1.5 border rounded-md bg-white text-sm"
            >
              {OUTCOMES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="col-span-3">
            <label className="text-xs text-slate-500">Our rank</label>
            <Input
              value={form.our_rank}
              onChange={(e) => setForm({ ...form, our_rank: e.target.value })}
              placeholder="L1 / L2 / L3"
            />
          </div>
          <div className="col-span-4">
            <label className="text-xs text-slate-500">Our bid value (₹)</label>
            <Input
              type="number"
              value={form.our_bid_value_inr}
              onChange={(e) => setForm({ ...form, our_bid_value_inr: e.target.value })}
              placeholder="e.g. 4350000"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
              Top bidders (rank order)
            </p>
            <Button size="sm" variant="outline" onClick={addBidder}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add bidder
            </Button>
          </div>
          {bidders.map((b, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 items-center p-2 rounded border bg-slate-50"
            >
              <div className="col-span-1 text-xs font-mono text-slate-500">L{idx + 1}</div>
              <Input
                className="col-span-5"
                value={b.name}
                onChange={(e) => update(idx, { name: e.target.value })}
                placeholder={b.is_us ? "AXISINDIA" : "Competitor name"}
              />
              <Input
                className="col-span-3"
                type="number"
                value={b.value_inr}
                onChange={(e) => update(idx, { value_inr: e.target.value })}
                placeholder="Bid value ₹"
              />
              <label className="col-span-2 inline-flex items-center gap-1 text-xs text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={b.is_us}
                  onChange={(e) => update(idx, { is_us: e.target.checked })}
                />
                Us
              </label>
              <div className="col-span-1 flex gap-1 justify-end">
                <Button
                  size="icon" variant="ghost" className="h-7 w-7"
                  onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="icon" variant="ghost" className="h-7 w-7"
                  onClick={() => move(idx, 1)} disabled={idx === bidders.length - 1} title="Move down"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="icon" variant="ghost" className="h-7 w-7"
                  onClick={() => removeBidder(idx)} disabled={bidders.length === 1} title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                </Button>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-slate-400">
            The server drops shape-valid rows with an empty name; structurally
            malformed entries are rejected at the API boundary.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Save outcome"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
